import type { TFunction } from 'i18next'
import type {
  Control,
  FieldValues,
  UseFormRegister,
  UseFormSetValue,
} from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import type { HTMLChakraProps } from '@enechain/ecloud-designsystem'
import {
  Box,
  HStack,
  Input,
  FieldErrorText as RawFieldErrorText,
  Text,
  VStack,
} from '@enechain/ecloud-designsystem'

import { DsSelect, type DsSelectOptions } from '~/components/common/DsSelect'
import { HintTooltip } from '~/components/common/HintTooltip'
import type {
  IntradayFormulaFieldChoices,
  IntradayFormulaFieldsState,
  IntradayFormulaPreviewLines,
} from '~/pages/IntradayBidding/hooks/useIntradayFormulaFields'
import { useIntradayFormulaFormBinding } from '~/pages/IntradayBidding/hooks/useIntradayFormulaFormBinding'
import {
  type FeeAutoConsiderHint,
  formatYenPerKwh,
} from '~/pages/IntradayBidding/models/feeAutoConsider'
import {
  FORMULA_OFFSET_STEP,
  FORMULA_OPERATORS,
  type FormulaPricePreview,
  type IntradayFormulaFieldValues,
  constrainFormulaOffsetInput,
  formulaOperatorSymbol,
  formulaReferenceAreaAfterVariableChange,
} from '~/pages/IntradayBidding/models/intradayFormula'

const FieldErrorText = RawFieldErrorText as React.FC<HTMLChakraProps<'div'>>

const FORMULA_OPERATOR_OPTIONS: DsSelectOptions = FORMULA_OPERATORS.map(
  (op) => ({ value: op, label: formulaOperatorSymbol(op) }),
)

// 最長の「システムプライス」 と記号 1 文字に、 DS Select の余白と矢印 (約 83px) と Meiryo の字幅の余裕を足した幅。
const FORMULA_NAME_SELECT_MIN_WIDTH = '210px'
const FORMULA_OPERATOR_SELECT_MIN_WIDTH = '120px'

/** 計算結果のプレビュー (design `#intraday-bnew-formula-preview`)。 */
const formulaPreviewText = (
  t: TFunction,
  preview: FormulaPricePreview,
): string => {
  switch (preview.kind) {
    case 'calculated':
      return t('page:biddingIntraday.form.formulaPreviewPrice', {
        price: formatYenPerKwh(preview.formulaPriceYenPerKwh),
      })
    case 'variableUnavailable':
      return t('page:biddingIntraday.form.formulaPreviewUnavailable')
    case 'notCalculable':
      return t('page:biddingIntraday.form.formulaPreviewNotCalculable')
    case 'pending':
    case 'failed':
      return t('page:biddingIntraday.form.formulaPreviewPending')
  }
}

const formFeeHintKey = (key: FeeAutoConsiderHint['key']): string =>
  `page:biddingIntraday.form.${key}`

type InputsProps = {
  fields: IntradayFormulaFieldChoices
  values: IntradayFormulaFieldValues
  /** 欄の値を変えた時。 変数を変えて参照エリアが戻る時は、 参照エリアの変更も続けて呼ぶ。 */
  onChange: (name: keyof IntradayFormulaFieldValues, value: string) => void
  /**
   * 1 コマの計算結果と補足 (AC-BIN-005c)。 一括作成はコマごとに結果が違うので渡さず、
   * プレビューの表で出す。
   */
  previewLines?: IntradayFormulaPreviewLines
  /** 入札価格の補足 (AC-BIN-004b) の文言 key。 既定は札作成の `form.*`。 */
  feeHintKey?: (key: FeeAutoConsiderHint['key']) => string
  /** オフセット値の欄の id (画面ごとに一意)。 */
  offsetInputId: string
  /** オフセット値の欄の ref (フォームが検証の失敗時に focus するため)。 */
  offsetInputRef?: React.Ref<HTMLInputElement>
  /** オフセット値の欄に出すエラー (入力の不足・入札価格の範囲外)。 */
  offsetError: string | undefined
}

/**
 * フォーミュラ欄の本体 (AC-BIN-005 / 005c): 変数・参照エリア・演算子・オフセット値と、
 * 計算結果・参照した値・入札価格の補足。 design `#intraday-bnew-price-formula` の並びに
 * 参照エリアを足す。 値の置き場 (React Hook Form の field の path・useState) は呼び出し側が
 * 決め、 ここは `values` を出して `onChange` で返すだけ。 選択肢は
 * [[useIntradayFormulaFieldChoices]] が持つ。
 */
export const IntradayFormulaInputs = ({
  fields,
  values,
  onChange,
  previewLines,
  feeHintKey = formFeeHintKey,
  offsetInputId,
  offsetInputRef,
  offsetError,
}: InputsProps): React.ReactElement => {
  const { t } = useTranslation()

  const handleVariableChange = (next: string | undefined): void => {
    onChange('formulaVariable', next ?? '')
    const chosen = values.formulaReferenceArea
    const after = formulaReferenceAreaAfterVariableChange(chosen, next)
    if (after !== chosen) onChange('formulaReferenceArea', after)
  }

  return (
    <VStack align="stretch" gap={2} marginTop={2}>
      {/* Meiryo でも変数名と記号が切れないよう 2 行に分け、 選択中の名前は hover でも読めるようにする。 */}
      <HStack align="flex-end" flexWrap="wrap" gap={2}>
        <VStack align="stretch" flex={1} gap={1}>
          <Text color="Text.midEmphasis" fontSize="xs" aria-hidden>
            {t('page:biddingIntraday.form.formulaVariable')}
          </Text>
          <HintTooltip content={fields.variableHint}>
            <Box title={fields.variableHint}>
              <DsSelect
                flexShrink={0}
                width="100%"
                minWidth={FORMULA_NAME_SELECT_MIN_WIDTH}
                aria-label={t('page:biddingIntraday.form.formulaVariable')}
                onChange={handleVariableChange}
                options={fields.variableOptions}
                value={values.formulaVariable}
              />
            </Box>
          </HintTooltip>
        </VStack>
        <VStack align="stretch" flex={1} gap={1}>
          <Text color="Text.midEmphasis" fontSize="xs" aria-hidden>
            {t('page:biddingIntraday.form.formulaReferenceArea')}
          </Text>
          <HintTooltip content={fields.referenceAreaHint}>
            <Box title={fields.referenceAreaHint}>
              <DsSelect
                flexShrink={0}
                width="100%"
                minWidth={FORMULA_NAME_SELECT_MIN_WIDTH}
                aria-label={t('page:biddingIntraday.form.formulaReferenceArea')}
                onChange={(next) =>
                  onChange('formulaReferenceArea', next ?? '')
                }
                options={fields.referenceAreaOptions}
                placeholder={t('common:form.selectPlaceholder')}
                value={fields.referenceAreaValue}
              />
            </Box>
          </HintTooltip>
        </VStack>
      </HStack>
      <HStack align="flex-end" flexWrap="wrap" gap={2}>
        <VStack align="stretch" flexShrink={0} gap={1}>
          <Text color="Text.midEmphasis" fontSize="xs" aria-hidden>
            {t('page:biddingIntraday.form.formulaOperator')}
          </Text>
          <HintTooltip content={fields.operatorHint}>
            <Box title={fields.operatorHint}>
              <DsSelect
                flexShrink={0}
                width={FORMULA_OPERATOR_SELECT_MIN_WIDTH}
                minWidth={FORMULA_OPERATOR_SELECT_MIN_WIDTH}
                aria-label={t('page:biddingIntraday.form.formulaOperator')}
                onChange={(next) => onChange('formulaOperator', next ?? '')}
                options={FORMULA_OPERATOR_OPTIONS}
                value={values.formulaOperator}
              />
            </Box>
          </HintTooltip>
        </VStack>
        <VStack align="stretch" flexShrink={0} gap={1} width="128px">
          <Text color="Text.midEmphasis" fontSize="xs" aria-hidden>
            {t('page:biddingIntraday.form.formulaOffset')}
          </Text>
          <Input
            ref={offsetInputRef}
            fontSize="md"
            textAlign="right"
            aria-label={t('page:biddingIntraday.form.formulaOffset')}
            id={offsetInputId}
            onChange={(event) =>
              onChange(
                'formulaOffset',
                constrainFormulaOffsetInput(event.target.value),
              )
            }
            paddingY="11px"
            step={FORMULA_OFFSET_STEP}
            type="number"
            value={values.formulaOffset}
          />
        </VStack>
        {fields.offsetHasYenUnit && (
          <Text color="Text.midEmphasis" fontSize="md" lineHeight="40px">
            {t('page:biddingIntraday.form.unitYen')}
          </Text>
        )}
        {previewLines !== undefined && (
          <Text
            color="Text.accentPrimary"
            fontWeight="bold"
            lineHeight="40px"
            whiteSpace="nowrap"
            fontVariantNumeric="tabular-nums"
          >
            {formulaPreviewText(t, previewLines.price)}
          </Text>
        )}
      </HStack>
      {previewLines?.referenceText !== undefined && (
        <Text color="Text.midEmphasis" fontSize="sm">
          {previewLines.referenceText}
        </Text>
      )}
      {/* AC-BIN-004b: 計算結果を希望価格とした入札価格の補足表示。 */}
      {previewLines?.feeHint !== undefined && (
        <Text color={previewLines.feeHint.color} fontSize="sm">
          {t(feeHintKey(previewLines.feeHint.key), {
            price: previewLines.feeHint.orderPriceYenPerKwh,
          })}
        </Text>
      )}
      <FieldErrorText whiteSpace="pre-line">{offsetError}</FieldErrorText>
    </VStack>
  )
}

type Props<T extends FieldValues & IntradayFormulaFieldValues> = {
  fields: IntradayFormulaFieldsState
  control: Control<T>
  register: UseFormRegister<T>
  setValue: UseFormSetValue<T>
  /** オフセット値の欄に出すエラー (入力の不足・入札価格の範囲外)。 */
  offsetError: string | undefined
  /** オフセット値の欄の id (画面ごとに一意)。 */
  offsetInputId: string
  /** 入札価格の補足 (AC-BIN-004b) の文言 key。 既定は札作成の `form.*`。 */
  feeHintKey?: (key: FeeAutoConsiderHint['key']) => string
  /** オフセット値を打ち替えた時 (modal の他の欄のエラー表示を消すのに使う)。 */
  onOffsetEdited?: () => void
}

/**
 * 1 コマの札 (札作成・入札編集) のフォーミュラ欄 ([[IntradayFormulaInputs]] を React Hook Form の form に
 * 繋いだもの)。 form は最上位にフォーミュラの 4 欄を持つ。 状態は [[useIntradayFormulaFields]] が持つ。
 */
export const IntradayFormulaFields = <
  T extends FieldValues & IntradayFormulaFieldValues,
>({
  fields,
  control,
  register,
  setValue,
  offsetError,
  offsetInputId,
  feeHintKey,
  onOffsetEdited,
}: Props<T>): React.ReactElement => {
  const binding = useIntradayFormulaFormBinding({ control, register, setValue })
  return (
    <IntradayFormulaInputs
      feeHintKey={feeHintKey}
      fields={fields}
      offsetError={offsetError}
      offsetInputId={offsetInputId}
      offsetInputRef={binding.offsetInputRef}
      onChange={(name, value) => {
        if (name === 'formulaOffset') onOffsetEdited?.()
        binding.onChange(name, value)
      }}
      previewLines={{
        price: fields.preview.price,
        referenceText: fields.referenceText,
        feeHint: fields.feeHint,
      }}
      values={binding.values}
    />
  )
}
